import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

/**
 * Auth-Context für Phase 5.
 * Verwendet AUSSCHLIESSLICH die zentrale axios-Instance aus lib/api.js
 * (mit `withCredentials: true`), damit httpOnly-Cookies bei JEDEM Request
 * deterministisch mitgesendet werden.
 *
 * Vorher gab es `axios.defaults.withCredentials = true` als globalen Default.
 * Das war fehleranfällig, weil andere Module ihren Default früher
 * importieren konnten und der Default daher nicht für sie galt.
 */

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  changePassword: async () => {},
  reload: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Globaler Cleanup: das session_expired-Flag wird vom Interceptor in lib/api.js
  // gesetzt, wenn ein API-Call 401 liefert. Wir leeren es konsequent bei jedem
  // erfolgreichen /auth/me und Login, damit kein veralteter Wert eine Schleife auslöst.
  const clearSessionFlag = useCallback(() => {
    try { window.sessionStorage.removeItem("session_expired"); } catch { /* ignore */ }
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      clearSessionFlag();
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [clearSessionFlag]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (username, passwort) => {
    const { data } = await api.post("/auth/login", { username, passwort });
    setUser(data.user);
    clearSessionFlag();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(null);
    clearSessionFlag();
  };

  const changePassword = async (altes_passwort, neues_passwort) => {
    await api.post("/auth/change-password", { altes_passwort, neues_passwort });
    setUser((u) => (u ? { ...u, muss_passwort_aendern: false } : u));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, changePassword, reload: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
